<template>
  <div class="d-flex fd-column:mo">
    <div class="CALENDAR-SECTION">
      <div v-if="!hideCalendar">
        <div v-if="$device.isMobile" class="d-flex fd-column">
          <div>
            <view-selection @view-change="onViewChange"/>
          </div>
          <div class="as-center">
            <span
                class="p-r-2"
                :class="[enablePrevious ? 'cur-pointer':'c-gray40']"
                @click="onPreviousClick">
              <vk-icon name="chevronleft"/>
            </span>
            <span class="h5:d text-bold p-r-2">{{ monthYear }}</span>
            <span class="cur-pointer" @click="onNextClick">
              <vk-icon name="chevronright"/>
            </span>
          </div>
        </div>
        <div v-else class="d-flex jc-between ai-center">
          <div>
            <span
                class="p-r-2"
                :class="[enablePrevious ? 'cur-pointer':'c-gray40']"
                @click="onPreviousClick">
              <vk-icon name="chevronleft"/>
            </span>
            <span class="cur-pointer" @click="onNextClick">
              <vk-icon name="chevronright"/>
            </span>
          </div>
          <span class="h5 text-bold">{{ monthYear }}</span>
          <div>
            <view-selection @view-change="onViewChange"/>
          </div>
        </div>
      </div>
      <div :class="{'HIDE-CALENDAR': hideCalendar}" @click="dayClick">
        <FullCalendar
            ref="calendar"
            class="CALENDAR-FORMATTING"
            :event-limit="3"
            :options="calendarOptions"
        >
        </FullCalendar>
      </div>
    </div>
    <div v-if="showList" class="LIST-SECTION">
      <div v-if="isFetching" >
        <transition name="FADE" mode="out-in">
          <day-agenda-list-placeholder/>
        </transition>
      </div>
      <div v-else >
        <day-agenda-list
            :day-appointments="todaysAppointments"
            :selectedDate="selectedDate"
            @onDelete="onDelete"
            @prevDay="prevDay"
            @nextDay="nextDay"/>
        <day-pause-list
            :day-pauses="todaysPauses"
            @onEdit="onEdit"
            @onPauseDelete="onPauseDelete"
        />
      </div>
    </div>
  </div>
</template>

<script>

import '@fullcalendar/core/vdom';
import { mapGetters, mapActions } from 'vuex';
import appointments from '@/mixins/appointments';
import {
  addDays,
  isSameDay,
  isBefore,
  isAfter,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  addMonths,
  startOfDay,
  endOfDay,
  addMinutes,
  isWithinInterval } from 'date-fns';
import job from '@/mixins/job';
import addUTCTimeZoneDesignator from '@/utils/add-utc-time-zone-designator';

import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ViewSelection from './ViewSelection';
import DayAgendaList from './DayAgendaList';
import DayPauseList from './DayPauseList';
import DayAgendaListPlaceholder from './DayAgendaListPlaceholder';

export default {
  name: 'month-display',
  components: {
    FullCalendar,
    ViewSelection,
    DayAgendaList,
    DayPauseList,
    DayAgendaListPlaceholder,
  },
  mixins: [
    appointments,
    job,
  ],
  props: {
    dateSelected: {
      type: Date,
      default: new Date(),
    },
    updateEvents: {
      type: Number,
    },
    hideCalendar: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      appointmentsBucketName: 'monthly',
      calendar: null,
      monthYear: null,
      isFetching: false,
      selectedDate: this.dateSelected,
      listKey: 0,
      calendarEvents: [{}],
      calendarOptions: {
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin, // needed for dateClick
        ],
        dayMaxEventRows: 3,
        timeZone: 'local',
        headerToolbar: false,
        height: 'auto',
        initialView: 'dayGridMonth',
        nextDayThreshold: '00:00:01',
        events: this.calendarEvents,
        editable: false,
        selectable: false,
        unselectAuto: false,
        showNonCurrentDates: false,
        weekends: true,
        dateClick: this.onDateClick,
        moreLinkContent (args) {
          const { num } = args;
          return `${num} more`;
        },
        datesSet: this.onChange,
      },
    };
  },
  computed: {
    ...mapGetters('entities', {
      appointmentEntities: 'appointments',
    }),
    ...mapGetters('account/pro', [
      'serviceProviderCatalogOccupations',
      'unavailabilities',
      'occupationUnavailabilities',
    ]),
    events () {
      if (!this.isFetching) {
        return this.appointmentList.concat(this.pauseList);
      }
      return [];
    },
    showList () {
      return (this.$device.isMobileOrTablet && this.hideCalendar) || !this.$device.isMobileOrTablet;
    },
    appointmentList () {
      if (!this.isFetching) {
        return this.rawAppointments.map((appointmentId) => {
          const appointment = this.appointmentEntities[appointmentId];
          const appointmentJob = this.workOrderEntities[appointment.order_hash];
          const start = new Date(addUTCTimeZoneDesignator(appointment.start_dtm));
          const end = new Date(addUTCTimeZoneDesignator(appointment.end_dtm));
          const formattedAppointment = {};
          formattedAppointment.allDay = false;
          formattedAppointment.classNames = isSameDay(start, end)
            ? ['APPOINTMENT', 'EVENT', 'DOT']
            : ['APPOINTMENT', 'EVENT'];
          formattedAppointment.start = start;
          formattedAppointment.end = addMinutes(end, 1);
          formattedAppointment.title = appointmentJob ? `${appointmentJob.location.first_name}` : '';
          return formattedAppointment;
        });
      }
      return [];
    },
    pauseList () {
      return this.occupationUnavailabilities.concat(this.unavailabilities).map((pause) => {
        const formattedPause = {};
        formattedPause.allDay = false;
        formattedPause.start = new Date(`${pause.startDate}T00:00`);
        formattedPause.title = pause.serviceProviderOccupationId
          ? `Pause - ${this.serviceProviderCatalogOccupations[pause.serviceProviderOccupationId].occupation.display_name}`
          : 'Pause - All Occupations';
        formattedPause.classNames = isSameDay(
          new Date(pause.startDate),
          new Date(pause.endDate)
        )
          ? ['PAUSE', 'DOT', 'EVENT'] : ['PAUSE', 'EVENT'];
        formattedPause.end = new Date(`${pause.endDate}T00:01`);

        return formattedPause;
      });
    },
    enablePrevious () {
      if (this.isFetching) {
        return false;
      }
      if (isAfter(new Date(this.selectedDate), startOfMonth(new Date()))) {
        return true;
      }
      const checkEvents = this.events.filter((event) => {
        return isBefore(new Date(event.start), startOfMonth(new Date(this.selectedDate)))
          && isAfter(new Date(event.end), endOfMonth(subMonths(new Date(), 1)));
      });
      return checkEvents.length;
    },
    serviceProviderOccupationIds () {
      return Object.keys(this.serviceProviderCatalogOccupations).join(',');
    },
    currentDay () {
      return new Date(this.selectedDate.setHours(0, 0, 1));
    },
    todaysAppointments () {
      return this.rawAppointments.filter((value) => {
        return isWithinInterval(this.currentDay, {
          start: startOfDay(new Date(
            addUTCTimeZoneDesignator(this.appointmentEntities[value].start_dtm)
          )),
          end: endOfDay(new Date(
            addUTCTimeZoneDesignator(this.appointmentEntities[value].end_dtm)
          )),
        });
      }).sort((a, b) => (new Date(a.start) - new Date(b.start)));
    },
    todaysPauses () {
      return this.unavailabilities.concat(this.occupationUnavailabilities)
        .filter(((value) => {
          return isWithinInterval(
            this.currentDay,
            {
              start: new Date(value.startDate.replace(/-/g, '/')),
              end: new Date(value.endDate.replace(/-/g, '/')).setHours(23),
            }
          );
        }))
        .sort((pauseA, pauseB) => {
          return (new Date(pauseA.startDate)
          - new Date(pauseB.startDate));
        });
    },
  },
  watch: {
    updateEvents: {
      async handler () {
        this.calendarEvents = [{}];
        await this.getEvents();
      },
    },
    isFetching: {
      handler () {
        this.calendarOptions.events = this.events;
      },
    },
  },
  async mounted () {
    this.calendar = this.$refs.calendar.getApi();
    await this.getEvents();
    this.selectedDate = this.dateSelected;
    this.monthYear = this.calendar.view.title;
    this.calendarOptions.events = this.events;
    this.calendar.select(this.selectedDate);
  },
  methods: {
    ...mapActions('account/pro', [
      'fetchServiceProviderUnavailabilities',
      'fetchServiceProviderOccupationUnavailabilities', 'resetUnavailabilities',
    ]),
    dayClick () {
      if (!this.showList) this.$emit('dayClick');
    },
    async getEvents () {
      this.isFetching = true;
      await Promise.all(
        [
          this.clearAppointments(),
          this.clearPauses(),
          this.loadAppointments(),
        ]
      );
      await Promise.all(
        [
          this.fetchSelectedOccupationPauses(),
          this.fetchPauses(),
        ]
      ).then(() => {
        setTimeout(() => {
          this.isFetching = false;
        }, 500);
      });
    },
    async loadAppointments (params) {
      const firstDay = startOfMonth(subMonths(new Date(this.selectedDate), 2));
      const lastDay = endOfMonth(addMonths(new Date(this.selectedDate), 1));
      firstDay.setHours(0, 0, 0, 0);
      try {
        const data = await this.fetchAppointmentsByDate({
          bucketName: this.appointmentsBucketName,
          params: {
            after: firstDay,
            before: lastDay,
            size: 80,
            ...params,
            sort: 'DESC',
          },
        });
        if (!data.entities.appointments) {
          return;
        }
        const ids = Object.values(data.entities.appointments)
          .map(appointment => appointment.order_hash);
        const noRepeatIds = [...new Set(ids)].join(',');
        this.fetchWorkOrderMatches({
          jobsStatusAlias: 'appointments',
          params: {
            order_hash: noRepeatIds,
            service_provider_id: this.serviceProviderId,
            size: 80,
          },
        });
        if (this.meta.hasMore) {
          await this.loadAppointments({ from: this.meta.count });
        }
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },

    async clearAppointments () {
      await this.resetAppointments({ bucketName: this.appointmentsBucketName });
    },
    async clearPauses () {
      await this.resetUnavailabilities();
    },
    async fetchPauses () {
      try {
        await this.fetchServiceProviderUnavailabilities();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    async fetchSelectedOccupationPauses () {
      try {
        const params = {
          from: 0,
          size: 100,
          service_provider_occupation_ids: this.serviceProviderOccupationIds,
        };
        await this.fetchServiceProviderOccupationUnavailabilities(params);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    onDateClick (evt) {
      this.selectedDate = evt.date || evt.start;
      this.calendar.select(new Date(this.selectedDate));
      this.$emit('date-change', this.selectedDate);
    },
    async onPreviousClick () {
      if (this.enablePrevious) {
        this.calendarEvents = [{}];
        this.calendar.prev();
        this.monthYear = this.calendar.view.title;
        await this.getEvents();
        this.upDateDay();
      }
    },
    async onNextClick () {
      await this.getEvents();
      this.calendarEvents = [{}];
      this.calendar.next();
      this.monthYear = this.calendar.view.title;
      this.upDateDay();
    },
    async upDateDay () {
      const nextDate = this.calendar.getDate();
      this.calendar.select(nextDate);
      this.selectedDate = nextDate;
    },
    onViewChange (option) {
      this.$emit('view-change', option);
    },
    nextDay () {
      const nextDay = addDays(new Date(this.selectedDate), 1);
      this.calendar.select(nextDay);
      this.selectedDate = nextDay;
    },
    prevDay () {
      const prevDay = subDays(new Date(this.selectedDate), 1);
      this.calendar.select(prevDay);
      this.selectedDate = prevDay;
    },
    onDelete (val) {
      this.$emit('onDelete', val);
    },
    onPauseDelete (val) {
      this.$emit('onPauseDelete', val);
    },
    onEdit (val) {
      this.$emit('onEdit', val);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";
  @import "./assets/calendar.scss";
  @import "~@/views/Dashboard/constants";

  .CALENDAR-SECTION {
    height: auto;
    max-width:750px ;
    width:auto;

    @include mobile-only {
      width: 95vw;
      height:auto;
      margin-left: auto;
      margin-right: auto;
      padding-bottom: $unit *2;
    }
  }
  .LIST-SECTION {
    flex-direction: column;
    margin: 6*$unit $unit  $unit  $unit;
    flex-grow: 1;
    min-width: 300px;
    @include mobile-only{
      margin: $unit;
      width: 95vw;
    }
  }
  .HIDE-CALENDAR {
    visibility: hidden;
    position: absolute;
  }
  :deep(.EVENT) {
    .fc-event-time, .fc-event-title{
      @include mobile-only {
        display: none;
      }
    }
  }
</style>
